"use client";

import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Checkbox,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  type ClusterDetails,
  CodePointTable,
} from "@/components/tools/text/code-point-table";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { toDelimited } from "@/lib/tool-kit/text/delimited";
import {
  loadUnicodeData,
  type UnicodeDatabase,
} from "@/lib/tool-kit/text/unicode-data";
import {
  type Cluster,
  type CodePointDetails,
  describeCodePoint,
  formatBytes,
  formatUnits,
  graphemeClusters,
  normalizationForms,
  textSize,
} from "@/lib/tool-kit/text/unicode-inspect";

/** The table stops here; the CSV download has every code point. */
const TABLE_LIMIT = 500;
const RESULT_PREVIEW = 120;

/** English strings. A translated content file overrides these through `ui`. */
export const UNICODE_CHARACTER_INSPECTOR_STRINGS = {
  inputLabel: "Text to inspect",
  inputPlaceholder: "Paste a character, a word, an emoji or a whole sentence",
  trySample: "Try a sample",
  sample:
    "Caf\u00E9 vs. Cafe\u0301 \u{1F469}\u{1F3FD}\u200D\u{1F4BB} \uFB01 \u211D",
  clear: "Clear",
  emptyPrompt: "Paste or type something above to see every character in it.",
  loading: "Loading character names from the Unicode Character Database…",
  loadError:
    "The character names couldn't be loaded, so names, categories, scripts and blocks are missing below. Everything else is complete.",
  retry: "Try again",
  versionNote: "Names and properties from Unicode {version}.",
  results: "Results",
  statGraphemes: "Characters (grapheme clusters)",
  statCodePoints: "Code points",
  statUtf16: "UTF-16 code units",
  statUtf8: "UTF-8 bytes",
  sizeNote:
    "People count grapheme clusters, JavaScript's .length counts UTF-16 code units, and databases and file sizes usually count UTF-8 bytes.",
  tableHeading: "Character by character",
  caption: "Each grapheme cluster, then the code points it is made of",
  clusterRow: "Character {number}: {text}",
  clusterCodePoints: "one: {count} code point|other: {count} code points",
  colChar: "Char",
  colCodePoint: "Code point",
  colName: "Name",
  colCategory: "Category",
  colScript: "Script",
  colBlock: "Block",
  colUtf8: "UTF-8",
  colDetails: "Details",
  showDetails: "Show",
  hideDetails: "Hide",
  detailsFor: "Details for {codePoint}",
  expandAll: "Show the details of every code point",
  detailUtf16: "UTF-16",
  detailHtml: "HTML named reference",
  detailHtmlDecimal: "HTML decimal reference",
  detailHtmlHex: "HTML hex reference",
  detailJs: "JavaScript",
  detailJsPair: "JavaScript (surrogate pair)",
  detailCss: "CSS",
  detailUrl: "URL (percent-encoded)",
  detailAge: "Added in Unicode",
  detailControl: "Control name",
  detailAbbreviation: "Abbreviation",
  detailCorrection: "Corrected name",
  detailAlternate: "Also known as",
  none: "None",
  unassignedAge: "Not assigned",
  truncated:
    "The table shows the first {count} code points. The CSV download has all of them.",
  normalizationHeading: "Normalization",
  normalizationHint:
    "NFC composes letters and accents into single code points and NFD splits them apart; NFKC and NFKD also replace compatibility characters, such as ﬁ and ℝ, with plain letters.",
  colForm: "Form",
  colResult: "Result",
  colChanged: "Changed?",
  changed: "Changed",
  same: "Same as input",
  downloadCsv: "Download CSV",
  csvFilename: "unicode-characters.csv",
  csvCluster: "Character number",
  csvCharacter: "Character",
  csvCategory: "General category",
} as const;

type Strings = typeof UNICODE_CHARACTER_INSPECTOR_STRINGS;
type LoadStatus = "idle" | "loading" | "ready" | "error";

/** Fetch the character database the first time there is text to inspect. */
function useUnicodeDatabase() {
  const [database, setDatabase] = useState<UnicodeDatabase | null>(null);
  const [status, setStatus] = useState<LoadStatus>("idle");
  const load = useCallback(() => {
    setStatus("loading");
    loadUnicodeData().then(
      (loaded) => {
        setDatabase(loaded);
        setStatus("ready");
      },
      () => setStatus("error")
    );
  }, []);
  useEffect(() => {
    if (status === "idle") {
      load();
    }
  }, [status, load]);
  return { database, status, retry: load };
}

/** Clusters up to `limit` code points in all. */
function firstClusters(clusters: readonly Cluster[], limit: number): Cluster[] {
  const kept: Cluster[] = [];
  let count = 0;
  for (const cluster of clusters) {
    if (count + cluster.codePoints.length > limit && kept.length > 0) {
      break;
    }
    kept.push(cluster);
    count += cluster.codePoints.length;
  }
  return kept;
}

function describeClusters(
  clusters: readonly Cluster[],
  database: UnicodeDatabase | null
): ClusterDetails[] {
  return clusters.map((cluster, position) => ({
    number: position + 1,
    text: cluster.text,
    index: cluster.index,
    codePoints: cluster.codePoints.map((codePoint) =>
      describeCodePoint(codePoint, database)
    ),
  }));
}

function csvRow(cluster: number, details: CodePointDetails): string[] {
  return [
    String(cluster),
    details.char,
    details.hex,
    details.name,
    details.category,
    details.script,
    details.block,
    details.age,
    formatBytes(details.utf8),
    formatUnits(details.utf16),
    details.entities.map((name) => `&${name};`).join(" "),
    details.htmlDecimal,
    details.htmlHex,
    details.javascript,
    details.css,
    details.url,
  ];
}

function downloadCsv(
  clusters: readonly Cluster[],
  database: UnicodeDatabase | null,
  strings: Strings
) {
  const header = [
    strings.csvCluster,
    strings.csvCharacter,
    strings.colCodePoint,
    strings.colName,
    strings.csvCategory,
    strings.colScript,
    strings.colBlock,
    strings.detailAge,
    strings.colUtf8,
    strings.detailUtf16,
    strings.detailHtml,
    strings.detailHtmlDecimal,
    strings.detailHtmlHex,
    strings.detailJs,
    strings.detailCss,
    strings.detailUrl,
  ];
  const rows = describeClusters(clusters, database).flatMap((cluster) =>
    cluster.codePoints.map((details) => csvRow(cluster.number, details))
  );
  downloadText(
    strings.csvFilename,
    `${toDelimited([header, ...rows], ",")}\n`,
    "text/csv;charset=utf-8"
  );
}

function Normalization({ text, strings }: { text: string; strings: Strings }) {
  const fmt = useToolNumber();
  const forms = useMemo(() => normalizationForms(text), [text]);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.normalizationHeading}</h3>
      <p className={HINT}>{strings.normalizationHint}</p>
      <DataTable
        head={[
          strings.colForm,
          strings.colResult,
          strings.statCodePoints,
          strings.statUtf16,
          strings.statUtf8,
          strings.colChanged,
        ]}
        rows={forms.map((form) => [
          form.form,
          form.text.length > RESULT_PREVIEW
            ? `${form.text.slice(0, RESULT_PREVIEW)}…`
            : form.text,
          fmt(form.codePoints),
          fmt(form.utf16),
          fmt(form.utf8),
          form.same ? strings.same : strings.changed,
        ])}
      />
    </div>
  );
}

function DatabaseStatus({
  status,
  version,
  retry,
  strings,
}: {
  status: LoadStatus;
  version: string;
  retry: () => void;
  strings: Strings;
}) {
  if (status === "error") {
    return (
      <div className="space-y-2">
        <ErrorNotice>{strings.loadError}</ErrorNotice>
        <button className={BUTTON_SECONDARY} onClick={retry} type="button">
          {strings.retry}
        </button>
      </div>
    );
  }
  if (status === "ready") {
    return <p className={HINT}>{t(strings.versionNote, { version })}</p>;
  }
  return <Notice>{strings.loading}</Notice>;
}

function Results({ text, strings }: { text: string; strings: Strings }) {
  const fmt = useToolNumber();
  const { database, status, retry } = useUnicodeDatabase();
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const clusters = useMemo(() => graphemeClusters(text), [text]);
  const size = useMemo(() => textSize(text), [text]);
  const shown = useMemo(() => firstClusters(clusters, TABLE_LIMIT), [clusters]);
  const details = useMemo(
    () => describeClusters(shown, database),
    [shown, database]
  );
  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <DatabaseStatus
        retry={retry}
        status={status}
        strings={strings}
        version={database?.version ?? ""}
      />
      <StatGrid>
        <StatCard
          emphasis
          label={strings.statGraphemes}
          value={fmt(size.graphemes)}
        />
        <StatCard label={strings.statCodePoints} value={fmt(size.codePoints)} />
        <StatCard label={strings.statUtf16} value={fmt(size.utf16)} />
        <StatCard label={strings.statUtf8} value={fmt(size.utf8)} />
      </StatGrid>
      <p className={HINT}>{strings.sizeNote}</p>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => downloadCsv(clusters, database, strings)}
            type="button"
          >
            {strings.downloadCsv}
          </button>
        </div>
        <Checkbox
          checked={expandAll}
          label={strings.expandAll}
          onChange={setExpandAll}
        />
        {shown.length < clusters.length ? (
          <p className={HINT}>
            {t(strings.truncated, { count: fmt(TABLE_LIMIT) })}
          </p>
        ) : null}
        <CodePointTable
          clusters={details}
          isOpen={(key) => expandAll || open.has(key)}
          onToggle={toggle}
          strings={strings}
        />
      </div>
      <Normalization strings={strings} text={text} />
    </section>
  );
}

export default function UnicodeCharacterInspectorWidget() {
  const strings = useToolUi(UNICODE_CHARACTER_INSPECTOR_STRINGS);
  const [input, setInput] = useState("");
  const text = useDeferredValue(input);
  return (
    <>
      <TextAreaField
        id="uci-input"
        label={strings.inputLabel}
        onChange={setInput}
        placeholder={strings.inputPlaceholder}
        rows={3}
        value={input}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={input.length === 0}
          onClick={() => setInput("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {text.length === 0 ? (
        <Notice>{strings.emptyPrompt}</Notice>
      ) : (
        <Results strings={strings} text={text} />
      )}
    </>
  );
}
